/**
 * Dealer Locator — interactive map + searchable dealer list.
 *
 * Renders Leaflet map markers and a sidebar card list from an external
 * dealer API. Supports geocoded search, type filters, and viewport-synced
 * card visibility. All API data is rendered via createElement/textContent
 * (XSS-safe — no innerHTML).
 *
 * Config is read from data-* attributes on the <dealer-locator> element.
 * Translations come from data-i18n-* attributes set in the Liquid template.
 *
 * @see sections/dealer-locator.liquid
 * @see assets/dealer-map.css
 */
if (!customElements.get("dealer-locator")) {
  customElements.define("dealer-locator", class DealerLocator extends HTMLElement {
    constructor() {
      super();

      /** @type {?L.Map} */
      this.map = null;
      /** @type {L.Marker[]} */
      this.markers = [];
      /** @type {Object[]} */
      this.dealersData = [];
      /** @type {string} */
      this.currentFilter = "both";
      /** @type {?{lat: number, lng: number}} */
      this.searchCoordinates = null;
      /** @type {?string} */
      this.selectedCountryCode = null;
      /** @type {Object[]} */
      this.suggestionResults = [];
      /** @type {number} */
      this.selectedSuggestionIndex = -1;
      /** @type {?AbortController} */
      this.abortController = null;
      /** @type {?AbortController} */
      this.geocodeAbortController = null;
      /** @type {?AbortController} */
      this.dealerAbortController = null;
      /** @type {?number} */
      this.moveEndTimeout = null;
      /** @type {boolean} */
      this.suppressMoveEnd = false;
      /** @type {number} */
      this.totalDealerCount = 0;
      /** @type {Map<number, HTMLElement>} */
      this.cardCache = new Map();
      /** @type {?number} */
      this.suppressSafetyTimeout = null;
      /** @type {?Function} */
      this.fitCompleteHandler = null;
      /** @type {?Function} */
      this.pendingLayoutCallback = null;
    }

    /** Reads config from data-* attributes, caches DOM refs, and boots the map. */
    connectedCallback() {
      this.config = {
        apiUrl: this.dataset.apiUrl || "",
        defaultLat: parseFloat(this.dataset.defaultLat) || 20,
        defaultLng: parseFloat(this.dataset.defaultLng) || 0,
        defaultZoom: parseInt(this.dataset.defaultZoom, 10) || 2,
        markerIcon: this.dataset.markerIcon || "",
        markerIconRetina: this.dataset.markerIconRetina || "",
        markerShadow: this.dataset.markerShadow || "",
        markerIconService: this.dataset.markerIconService || "",
        markerIconServiceRetina: this.dataset.markerIconServiceRetina || ""
      };

      this.t = {
        noDealersFound: this.dataset.i18nNoDealersFound || "No dealers found in this area.",
        noVisibleDealers: this.dataset.i18nNoVisibleDealers || "No dealers visible in this area. Zoom out or pan the map.",
        hideMap: this.dataset.i18nHideMap || "Hide map",
        viewMap: this.dataset.i18nViewMap || "View results on map",
        getDirections: this.dataset.i18nGetDirections || "Get directions",
        distanceAway: this.dataset.i18nDistanceAway || "Distance: [distance] km away",
        distanceValue: this.dataset.i18nDistanceValue || "[distance] km",
        locationNotFound: this.dataset.i18nLocationNotFound || "Location not found. Please try a different search.",
        loadError: this.dataset.i18nLoadError || "Failed to load dealers. Please try again.",
        searchError: this.dataset.i18nSearchError || "Failed to search location. Please try again.",
        companyLabel: this.dataset.i18nCompanyLabel || "Company: ",
        addressLabel: this.dataset.i18nAddressLabel || "Address:",
        phoneLabel: this.dataset.i18nPhoneLabel || "Phone: ",
        emailLabel: this.dataset.i18nEmailLabel || "Email: ",
        websiteLabel: this.dataset.i18nWebsiteLabel || "Website: ",
        distanceLabel: this.dataset.i18nDistanceLabel || "Distance: ",
        showingCountOne: this.dataset.i18nShowingCountOne || "Showing [current] of [total] dealer",
        showingCountOther: this.dataset.i18nShowingCountOther || "Showing [current] of [total] dealers",
        typeBoth: this.dataset.i18nTypeBoth || "Dealer & Service"
      };

      this.css = {
        card: "dealer-card",
        noResults: "dealer-locator__no-results",
        suggestionItem: "dealer-locator__suggestion-item",
        searchGroup: "dealer-locator__search-group",
        filterBtn: "dealer-locator__filter-btn",
        active: "active",
        visible: "is-visible",
        inputError: "dealer-locator__input--error",
        badgeType: "dealer-badge dealer-badge--type",
        link: "link link-text show-underline"
      };

      this.searchInput = this.querySelector("[data-dealer-search-input]");
      this.suggestionsContainer = this.querySelector("[data-search-suggestions]");
      this.filterContainer = this.querySelector("[data-filter-buttons]");
      this.searchBtn = this.querySelector("[data-search-btn]");
      this.resetBtn = this.querySelector("[data-reset-btn]");
      this.radiusSelect = this.querySelector("[data-radius-select]");
      this.resultsCount = this.querySelector("[data-results-count]");
      this.resultsText = this.querySelector("[data-results-text]");
      this.dealersList = this.querySelector("[data-dealers-list]");
      this.mapContainer = this.querySelector("[data-dealer-map]");
      this.mapWrapper = this.querySelector("[data-map-wrapper]");
      this.mapLoading = this.querySelector("[data-map-loading]");
      this.mapToggle = this.querySelector("[data-map-toggle]");
      this.mapToggleText = this.querySelector("[data-map-toggle-text]");
      this.modalDialog = this.querySelector("modal-dialog");
      this.modalBody = this.querySelector("[data-dealer-modal-body]");

      const required = ["searchInput", "suggestionsContainer", "filterContainer",
        "searchBtn", "resetBtn", "dealersList", "mapContainer", "mapWrapper",
        "mapLoading", "mapToggle", "mapToggleText", "modalDialog", "modalBody"];
      const missing = required.filter((ref) => !this[ref]);
      if (missing.length > 0) {
        console.error(`Dealer locator: missing elements: ${missing.join(", ")}`);
        return;
      }

      // Read ?location= URL parameter from dealer-map-search block redirect
      const urlParams = new URLSearchParams(window.location.search);
      this.initialSearchQuery = urlParams.get("location") || null;

      if (this.initialSearchQuery && this.searchInput) {
        this.searchInput.value = this.initialSearchQuery;
      }

      this.debouncedSuggestionFetch = webvista.debounce((query, signal) => {
        fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(query)}&limit=5`, {
          signal
        })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          })
          .then((results) => {
            if (results && results.length > 0) {
              this.suggestionResults = results;
              this.showSuggestions(results);
            } else {
              this.hideSuggestions();
            }
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              console.error("Autocomplete error:", error);
              this.hideSuggestions();
            }
          });
      }, 300);

      this.bindEventListeners();

      if (typeof L !== "undefined") {
        this.initMap();
      } else if (document.readyState === "loading") {
        this.boundOnLeafletReady = () => this.onLeafletReady();
        document.addEventListener("DOMContentLoaded", this.boundOnLeafletReady);
      } else {
        console.warn("Dealer locator: Leaflet (L) is not available.");
      }
    }

    /** Aborts in-flight requests, tears down the map, and removes all listeners. */
    disconnectedCallback() {
      this.abortAllRequests();
      if (this.modalDialog && this.modalDialog.hasAttribute("open")) {
        this.modalDialog.hide();
      }
      this.unbindEventListeners();
      this.destroyMap();
      if (this.boundOnLeafletReady) {
        document.removeEventListener("DOMContentLoaded", this.boundOnLeafletReady);
        this.boundOnLeafletReady = null;
      }
    }

    /** Aborts all in-flight fetch requests and clears their controllers. */
    abortAllRequests() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      if (this.geocodeRetryTimeout) {
        clearTimeout(this.geocodeRetryTimeout);
        this.geocodeRetryTimeout = null;
      }
      if (this.geocodeAbortController) {
        this.geocodeAbortController.abort();
        this.geocodeAbortController = null;
      }
      if (this.dealerAbortController) {
        this.dealerAbortController.abort();
        this.dealerAbortController = null;
      }
    }

    /** DOMContentLoaded handler — initialises the map once Leaflet is available. */
    onLeafletReady() {
      if (typeof L !== "undefined" && !this.map) {
        this.initMap();
      }
    }

    /**
     * Creates the Leaflet map instance, adds the tile layer, loads dealers,
     * and defers fitBounds until the container has non-zero dimensions.
     */
    initMap() {
      // Explicit icons bypass L.Icon.Default auto-detection which parses
      // leaflet.css background-image URLs instead of our Shopify CDN assets
      const iconOptions = {
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
        shadowUrl: this.config.markerShadow
      };

      this.markerIcons = {
        dealer: L.icon({ ...iconOptions,
          iconUrl: this.config.markerIcon,
          iconRetinaUrl: this.config.markerIconRetina
        }),
        service: L.icon({ ...iconOptions,
          iconUrl: this.config.markerIconService || this.config.markerIcon,
          iconRetinaUrl: this.config.markerIconServiceRetina || this.config.markerIconRetina
        })
      };

      this.map = L.map(this.mapContainer, {
        minZoom: 2,
        maxBounds: [[-85, -540], [85, 540]],
        maxBoundsViscosity: 1.0
      }).setView(
        [this.config.defaultLat, this.config.defaultLng],
        this.config.defaultZoom
      );

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a> contributors",
        maxZoom: 19
      }).addTo(this.map);

      this.boundOnMapMoveEnd = () => this.onMapMoveEnd();
      this.map.on("moveend", this.boundOnMapMoveEnd);

      if (this.initialSearchQuery) {
        this.searchLocation(this.initialSearchQuery, { silent: true });
      } else {
        this.loadDealers();
      }

      this.waitForLayout(() => {
        if (this.map) {
          this.map.invalidateSize();
          if (this.markers.length > 0) {
            this.fitMapToMarkers();
          }
        }
      });
    }

    /**
     * Polls via rAF until the map container has non-zero dimensions, then
     * invokes the callback. If the container is detached (display:none on
     * mobile), stores the callback for {@link toggleMap} to execute later.
     *
     * @param {Function} callback - Runs once the container is laid out.
     */
    waitForLayout(callback) {
      const el = this.mapContainer;
      let retries = 0;
      const maxRetries = 60;
      const check = () => {
        if (!this.map) return;
        if (el.clientWidth > 0 && el.clientHeight > 0) {
          callback();
        } else if (el.offsetParent === null) {
          this.pendingLayoutCallback = callback;
        } else if (retries < maxRetries) {
          retries++;
          requestAnimationFrame(check);
        }
      };
      requestAnimationFrame(check);
    }

    /** Removes the Leaflet map instance, clears timers, and resets state. */
    destroyMap() {
      if (this.moveEndTimeout) {
        clearTimeout(this.moveEndTimeout);
        this.moveEndTimeout = null;
      }
      clearTimeout(this.suppressSafetyTimeout);
      this.suppressSafetyTimeout = null;
      this.fitCompleteHandler = null;
      this.suppressMoveEnd = false;
      if (this.map) {
        this.map.off("moveend", this.boundOnMapMoveEnd);
        this.map.remove();
        this.map = null;
      }
      this.markers = [];
    }

    /** Removes all markers from the map and empties the markers array. */
    clearMarkers() {
      if (this.map) {
        this.markers.forEach((marker) => this.map.removeLayer(marker));
      }
      this.markers = [];
    }

    /**
     * @returns {boolean} Whether the map container has non-zero dimensions.
     */
    isMapVisible() {
      return this.mapContainer.clientWidth > 0 && this.mapContainer.clientHeight > 0;
    }

    /**
     * Validates that a string is an absolute HTTP(S) URL.
     * @param {string} url
     * @returns {boolean}
     */
    isHttpUrl(url) {
      try {
        const parsed = new URL(url);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
      } catch (e) {
        return false;
      }
    }

    /**
     * Builds a "city, country" string from a dealer object.
     * @param {Object} dealer
     * @returns {string}
     */
    buildCityCountry(dealer) {
      return [dealer.city, dealer.country_name || dealer.country]
        .filter(Boolean).join(", ");
    }

    /**
     * Returns the display label for a dealer type.
     * @param {string} type - Raw dealer_type value from the API.
     * @returns {string}
     */
    dealerTypeLabel(type) {
      return type === "both" ? this.t.typeBoth : (type || "");
    }

    /**
     * Fetches dealers from the API. Uses `/dealers/search` when coordinates
     * are present, otherwise `/dealers` for the full list.
     *
     * @param {Object} [params={}] - Query parameters (lat, lng, radius, dealer_type).
     */
    loadDealers(params = {}) {
      if (!this.config.apiUrl || this.config.apiUrl.includes("your-app-domain")) return;

      if (this.dealerAbortController) {
        this.dealerAbortController.abort();
      }
      this.dealerAbortController = new AbortController();

      this.showLoading(true);

      const queryParams = new URLSearchParams(params);
      const endpoint = params.lat && params.lng ? "/dealers/search" : "/dealers";

      const url = `${this.config.apiUrl}${endpoint}?${queryParams}`;
      const signal = this.dealerAbortController.signal;

      const attemptFetch = (attempt) => {
        fetch(url, { method: "GET", signal })
          .then((response) => {
            if (response.status === 425 && attempt === 0) {
              attemptFetch(1);
              return;
            }
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          })
          .then((data) => {
            if (!data) return;
            this.showLoading(false);
            if (!this.map) return;
            if (data.success) {
              this.dealersData = data.data;
              this.totalDealerCount = data.meta?.total ?? this.dealersData.length;
              this.displayDealers(this.dealersData);
            } else {
              this.showError(data.message || this.t.loadError);
            }
          })
          .catch((error) => {
            if (error.name === "AbortError") return;
            console.error("Error loading dealers:", error);
            this.showLoading(false);
            this.showError(this.t.loadError);
          });
      };

      attemptFetch(0);
    }

    /** Builds search params from the current filter/coordinates and loads dealers. */
    performSearch() {
      const params = {};

      if (this.currentFilter !== "both") {
        params.dealer_type = this.currentFilter;
      }

      if (this.searchCoordinates) {
        params.lat = this.searchCoordinates.lat;
        params.lng = this.searchCoordinates.lng;

        if (this.selectedCountryCode) {
          params.country = this.selectedCountryCode;
        } else if (this.radiusSelect) {
          params.radius = this.radiusSelect.value;
        }
      }

      this.loadDealers(params);
    }

    /**
     * Clears existing markers, places new ones, and schedules a card sync
     * after fitBounds completes. Suppresses intermediate moveend events
     * during the fitBounds animation.
     *
     * @param {Object[]} dealers - Dealer objects from the API.
     */
    displayDealers(dealers) {
      if (!this.map) return;

      this.clearMarkers();
      this.cardCache.clear();
      this.dealersList.replaceChildren();

      if (dealers.length === 0) {
        const noResults = document.createElement("div");
        noResults.className = this.css.noResults;
        noResults.textContent = this.t.noDealersFound;
        this.dealersList.appendChild(noResults);
        this.updateVisibleCount(0);
        return;
      }

      this.suppressMoveEnd = true;

      if (this.fitCompleteHandler) {
        this.map.off("moveend", this.fitCompleteHandler);
        this.fitCompleteHandler = null;
      }
      clearTimeout(this.suppressSafetyTimeout);

      this.renderMarkers(dealers);

      if (this.markers.length > 0) {
        // One-shot listener syncs cards once fitBounds animation completes
        this.fitCompleteHandler = () => {
          clearTimeout(this.suppressSafetyTimeout);
          this.fitCompleteHandler = null;
          this.syncCardsToViewport();
          this.suppressMoveEnd = false;
        };
        this.map.once("moveend", this.fitCompleteHandler);

        // Safety net: if fitBounds was a no-op moveend never fires
        this.suppressSafetyTimeout = setTimeout(() => {
          if (this.suppressMoveEnd && this.fitCompleteHandler) {
            this.map.off("moveend", this.fitCompleteHandler);
            this.fitCompleteHandler = null;
            this.syncCardsToViewport();
            this.suppressMoveEnd = false;
          }
        }, 600);
      } else {
        this.syncCardsToViewport();
        this.suppressMoveEnd = false;
      }
    }

    /**
     * Creates Leaflet markers for each dealer with valid coordinates and
     * fits the map to their bounds.
     *
     * @param {Object[]} dealers
     */
    renderMarkers(dealers) {
      dealers.forEach((dealer, i) => {
        const lat = parseFloat(dealer.latitude);
        const lng = parseFloat(dealer.longitude);
        if (isFinite(lat) && isFinite(lng)) {
          const icon = this.markerIcons[dealer.dealer_type] || this.markerIcons.dealer;
          const marker = L.marker([lat, lng], { icon })
            .addTo(this.map)
            .on("click", () => this.showDealerModal(dealer));
          marker._dealerIndex = i;
          this.markers.push(marker);
        }
      });

      if (this.markers.length > 0) {
        this.fitMapToMarkers();
      }
    }

    /**
     * Rebuilds the dealer card list to match the current map viewport.
     * On mobile (map hidden), shows all cards. Reuses cached card elements
     * to avoid redundant DOM creation on pan/zoom.
     */
    syncCardsToViewport() {
      if (!this.map) return;

      const filterByBounds = this.isMapVisible();
      const bounds = filterByBounds ? this.map.getBounds() : null;
      const fragment = document.createDocumentFragment();
      let visibleCount = 0;

      this.markers.forEach((marker) => {
        if (!filterByBounds || bounds.contains(marker.getLatLng())) {
          const idx = marker._dealerIndex;
          const dealer = this.dealersData[idx];
          if (dealer) {
            let card = this.cardCache.get(idx);
            if (!card) {
              card = this.createDealerCard(dealer);
              card.dataset.dealerIndex = idx;
              this.cardCache.set(idx, card);
            }
            fragment.appendChild(card);
            visibleCount++;
          }
        }
      });

      this.dealersList.replaceChildren();

      if (visibleCount === 0) {
        const noResults = document.createElement("div");
        noResults.className = this.css.noResults;
        noResults.textContent = filterByBounds
          ? this.t.noVisibleDealers
          : this.t.noDealersFound;
        this.dealersList.appendChild(noResults);
      } else {
        this.dealersList.appendChild(fragment);
      }

      this.updateVisibleCount(visibleCount);
    }

    /** Debounced moveend handler — syncs cards 150ms after the map stops moving. */
    onMapMoveEnd() {
      if (this.suppressMoveEnd) return;

      clearTimeout(this.moveEndTimeout);
      this.moveEndTimeout = setTimeout(() => {
        this.syncCardsToViewport();
      }, 150);
    }

    /**
     * Fits the map to the bounding box of all current markers.
     * Defers to {@link waitForLayout} if the container has zero dimensions.
     */
    fitMapToMarkers() {
      if (!this.map || this.markers.length === 0) return;

      const size = this.map.getSize();
      if (size.x === 0 || size.y === 0) {
        this.waitForLayout(() => this.fitMapToMarkers());
        return;
      }

      // Direct latLngBounds avoids featureGroup .pad() which can fail
      // Leaflet's internal instanceof check after section reloads
      const latlngs = this.markers.map((marker) => marker.getLatLng());
      const bounds = L.latLngBounds(latlngs);

      if (bounds.isValid()) {
        try {
          this.map.fitBounds(bounds, { maxZoom: 15, padding: [20, 20] });
        } catch (e) {
          // Non-fatal — markers are visible, map just won't auto-zoom
        }
      }
    }

    /**
     * Builds a dealer card element (XSS-safe via createElement/textContent).
     *
     * @param {Object} dealer - Dealer object from the API.
     * @returns {HTMLElement}
     */
    createDealerCard(dealer) {
      const card = document.createElement("div");
      card.className = this.css.card;
      card.setAttribute("role", "button");
      card.setAttribute("tabindex", "0");

      if (dealer.image_url && this.isHttpUrl(dealer.image_url)) {
        const img = document.createElement("img");
        img.src = dealer.image_url;
        img.alt = dealer.title || "";
        img.className = "dealer-card__image";
        card.appendChild(img);
      }

      const content = document.createElement("div");
      content.className = "dealer-card__content";

      const title = document.createElement("p");
      title.className =
        "dealer-card__title font-heading heading-block heading-size--mini";
      title.textContent = dealer.title || "";
      content.appendChild(title);

      const type = document.createElement("span");
      const safeType = (dealer.dealer_type || "").replace(/[^a-z0-9-]/gi, "");
      type.className = `dealer-card__type dealer-card__type-${safeType} ${this.css.badgeType}`;
      type.textContent = this.dealerTypeLabel(dealer.dealer_type);
      content.appendChild(type);

      if (dealer.distance) {
        const distance = document.createElement("p");
        distance.className = "dealer-card__distance";
        distance.textContent = this.t.distanceAway.replace("[distance]", dealer.distance.toFixed(2));
        content.appendChild(distance);
      }

      const contactInfo = document.createElement("address");
      contactInfo.className = "dealer-card__contact";

      const address = document.createElement("p");
      address.className = "dealer-card__address";
      const addressParts = [];
      if (dealer.address) addressParts.push(dealer.address);
      const cityCountry = this.buildCityCountry(dealer);
      if (cityCountry) addressParts.push(cityCountry);
      address.textContent = addressParts.join(", ");
      contactInfo.appendChild(address);

      if (dealer.phone) {
        const phone = document.createElement("p");
        phone.className = "dealer-card__phone";
        phone.textContent = dealer.phone;
        contactInfo.appendChild(phone);
      }

      const lat = parseFloat(dealer.latitude);
      const lng = parseFloat(dealer.longitude);
      if (isFinite(lat) && isFinite(lng)) {
        const directions = document.createElement("a");
        directions.href = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
        directions.target = "_blank";
        directions.rel = "noopener noreferrer";
        directions.setAttribute("aria-label", `${this.t.getDirections} (opens in new tab)`);
        directions.className = `dealer-card__directions ${this.css.link}`;
        directions.textContent = this.t.getDirections;
        directions.addEventListener("click", (e) => e.stopPropagation());
        contactInfo.appendChild(directions);
      }

      content.appendChild(contactInfo);
      card.appendChild(content);

      return card;
    }

    /**
     * Populates the dealer detail modal and opens it.
     *
     * @param {Object} dealer - Dealer object from the API.
     * @param {HTMLElement} [opener] - Element that triggered the modal (for focus restoration).
     */
    showDealerModal(dealer, opener) {
      this.modalBody.replaceChildren();

      if (dealer.image_url && this.isHttpUrl(dealer.image_url)) {
        const img = document.createElement("img");
        img.src = dealer.image_url;
        img.alt = dealer.title || "";
        img.className = "dealer-modal__image";
        this.modalBody.appendChild(img);
      }

      const title = document.createElement("p");
      title.className =
        "dealer-modal__title font-heading heading-block heading-size--small";
      title.textContent = dealer.title || "";
      this.modalBody.appendChild(title);

      const badges = document.createElement("div");
      badges.className = "dealer-modal__badges";

      const typeBadge = document.createElement("span");
      const modalSafeType = (dealer.dealer_type || "").replace(/[^a-z0-9-]/gi, "");
      typeBadge.className = `dealer-card__type-${modalSafeType} ${this.css.badgeType}`;
      typeBadge.textContent = this.dealerTypeLabel(dealer.dealer_type);
      badges.appendChild(typeBadge);

      if (dealer.status) {
        const statusBadge = document.createElement("span");
        statusBadge.className = "dealer-badge dealer-badge--status";
        statusBadge.textContent = dealer.status;
        badges.appendChild(statusBadge);
      }

      this.modalBody.appendChild(badges);

      if (dealer.description) {
        const desc = document.createElement("p");
        desc.className = "dealer-modal__description";
        desc.textContent = dealer.description;
        this.modalBody.appendChild(desc);
      }

      if (dealer.company_name) {
        const company = document.createElement("p");
        const companyStrong = document.createElement("strong");
        companyStrong.textContent = this.t.companyLabel;
        company.appendChild(companyStrong);
        company.appendChild(document.createTextNode(dealer.company_name));
        this.modalBody.appendChild(company);
      }

      const addressP = document.createElement("p");
      const addressStrong = document.createElement("strong");
      addressStrong.textContent = this.t.addressLabel;
      addressP.appendChild(addressStrong);
      addressP.appendChild(document.createElement("br"));
      if (dealer.address) {
        addressP.appendChild(document.createTextNode(dealer.address));
        addressP.appendChild(document.createElement("br"));
      }
      const cityCountry = this.buildCityCountry(dealer);
      if (cityCountry) {
        addressP.appendChild(document.createTextNode(cityCountry));
      }
      this.modalBody.appendChild(addressP);

      if (dealer.phone) {
        const phoneP = document.createElement("p");
        const phoneStrong = document.createElement("strong");
        phoneStrong.textContent = this.t.phoneLabel;
        phoneP.appendChild(phoneStrong);
        const phoneLink = document.createElement("a");
        phoneLink.className = this.css.link;
        phoneLink.href = `tel:${dealer.phone.replace(/\s/g, "")}`;
        phoneLink.textContent = dealer.phone;
        phoneP.appendChild(phoneLink);
        this.modalBody.appendChild(phoneP);
      }

      if (dealer.email) {
        const emailP = document.createElement("p");
        const emailStrong = document.createElement("strong");
        emailStrong.textContent = this.t.emailLabel;
        emailP.appendChild(emailStrong);
        const emailLink = document.createElement("a");
        emailLink.className = this.css.link;
        emailLink.href = `mailto:${dealer.email}`;
        emailLink.textContent = dealer.email;
        emailP.appendChild(emailLink);
        this.modalBody.appendChild(emailP);
      }

      if (dealer.website && this.isHttpUrl(dealer.website)) {
        const websiteP = document.createElement("p");
        const websiteStrong = document.createElement("strong");
        websiteStrong.textContent = this.t.websiteLabel;
        websiteP.appendChild(websiteStrong);
        const websiteLink = document.createElement("a");
        websiteLink.className = this.css.link;
        websiteLink.href = dealer.website;
        websiteLink.target = "_blank";
        websiteLink.rel = "noopener noreferrer";
        websiteLink.textContent = dealer.website;
        websiteP.appendChild(websiteLink);
        this.modalBody.appendChild(websiteP);
      }

      if (dealer.distance) {
        const distP = document.createElement("p");
        const distStrong = document.createElement("strong");
        distStrong.textContent = this.t.distanceLabel;
        distP.appendChild(distStrong);
        distP.appendChild(document.createTextNode(this.t.distanceValue.replace("[distance]", dealer.distance.toFixed(2))));
        this.modalBody.appendChild(distP);
      }

      const modalLat = parseFloat(dealer.latitude);
      const modalLng = parseFloat(dealer.longitude);
      if (isFinite(modalLat) && isFinite(modalLng)) {
        const directionsLink = document.createElement("a");
        directionsLink.href = `https://www.google.com/maps/dir/?api=1&destination=${modalLat},${modalLng}`;
        directionsLink.target = "_blank";
        directionsLink.rel = "noopener noreferrer";
        directionsLink.className = "button button--small dealer-modal__directions";
        directionsLink.textContent = this.t.getDirections;
        this.modalBody.appendChild(directionsLink);
      }

      this.modalDialog.show(opener);
    }

    /**
     * Geocodes a query via Nominatim and triggers a dealer search at the
     * resulting coordinates.
     *
     * @param {string} query - Free-text location string.
     * @param {Object} [options={}] - Options. Set `silent: true` to suppress errors and fall back to all dealers.
     */
    searchLocation(query, options = {}) {
      if (!query.trim()) return;

      if (this.geocodeAbortController) this.geocodeAbortController.abort();
      this.geocodeAbortController = new AbortController();

      this.showLoading(true);
      this.hideSuggestions();

      const signal = this.geocodeAbortController.signal;
      const retries = options.silent ? 1 : 0;

      const attemptGeocode = (attempt) => {
        fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(query)}`, { signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          })
          .then((results) => {
            if (results && results.length > 0) {
              const location = results[0];
              this.searchCoordinates = {
                lat: parseFloat(location.lat),
                lng: parseFloat(location.lon)
              };
              if (location.addresstype === "country" && location.address && location.address.country_code) {
                this.selectedCountryCode = location.address.country_code.toUpperCase();
              } else {
                this.selectedCountryCode = null;
              }
              this.performSearch();
            } else if (options.silent) {
              this.loadDealers();
            } else {
              this.showLoading(false);
              this.showError(this.t.locationNotFound);
            }
          })
          .catch((error) => {
            if (error.name === "AbortError") return;
            if (attempt < retries) {
              this.geocodeRetryTimeout = setTimeout(() => attemptGeocode(attempt + 1), 1000);
              return;
            }
            if (options.silent) {
              this.loadDealers();
            } else {
              console.error("Geocoding error:", error);
              this.showLoading(false);
              this.showError(this.t.searchError);
            }
          });
      };

      attemptGeocode(0);
    }

    /**
     * Debounced autocomplete — fetches Nominatim suggestions for queries
     * of 3+ characters. Aborts any in-flight request first.
     *
     * @param {string} query
     */
    fetchSuggestions(query) {
      if (!query || query.length < 3) {
        this.hideSuggestions();
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.debouncedSuggestionFetch(query, this.abortController.signal);
    }

    /**
     * Renders autocomplete suggestion items in the dropdown.
     *
     * @param {Object[]} results - Nominatim search results.
     */
    showSuggestions(results) {
      this.suggestionsContainer.replaceChildren();
      this.selectedSuggestionIndex = -1;

      const fragment = document.createDocumentFragment();
      results.forEach((result, i) => {
        const item = document.createElement("div");
        item.className = this.css.suggestionItem;
        item.textContent = result.display_name;
        item.dataset.index = i;
        fragment.appendChild(item);
      });
      this.suggestionsContainer.appendChild(fragment);

      this.suggestionsContainer.removeAttribute("hidden");
    }

    /**
     * Accepts a suggestion — sets the search input, stores coordinates,
     * and triggers a search.
     *
     * @param {Object} result - Nominatim result with lat/lon/display_name.
     */
    selectSuggestion(result) {
      this.searchInput.value = result.display_name;
      this.searchCoordinates = {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon)
      };
      if (result.addresstype === "country" && result.address && result.address.country_code) {
        this.selectedCountryCode = result.address.country_code.toUpperCase();
      } else {
        this.selectedCountryCode = null;
      }
      this.hideSuggestions();
      this.performSearch();
    }

    /** Applies the `active` class to the currently highlighted suggestion. */
    updateSuggestionHighlight() {
      const items = this.suggestionsContainer.querySelectorAll(`.${this.css.suggestionItem}`);
      items.forEach((item, i) => {
        item.classList.toggle(this.css.active, i === this.selectedSuggestionIndex);
      });
    }

    /**
     * Moves the suggestion highlight up or down (clamped to bounds).
     *
     * @param {"up"|"down"} direction
     */
    navigateSuggestions(direction) {
      const items = this.suggestionsContainer.querySelectorAll(`.${this.css.suggestionItem}`);
      if (items.length === 0) return;

      if (direction === "down") {
        this.selectedSuggestionIndex = Math.min(this.selectedSuggestionIndex + 1, items.length - 1);
      } else if (direction === "up") {
        this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, -1);
      }

      this.updateSuggestionHighlight();

      if (this.selectedSuggestionIndex >= 0) {
        items[this.selectedSuggestionIndex].scrollIntoView({ block: "nearest" });
      }
    }

    /** Hides the suggestions dropdown and resets selection state. */
    hideSuggestions() {
      this.suggestionsContainer.setAttribute("hidden", "");
      this.suggestionsContainer.replaceChildren();
      this.suggestionResults = [];
      this.selectedSuggestionIndex = -1;
    }

    /**
     * Toggles the loading spinner visibility.
     *
     * @param {boolean} show
     */
    showLoading(show) {
      if (show) {
        this.mapLoading.removeAttribute("hidden");
      } else {
        this.mapLoading.setAttribute("hidden", "");
      }
    }

    /**
     * Displays an error message via the theme's toast system.
     *
     * @param {string} message
     */
    showError(message) {
      webvista.popToast(message, "error");
    }

    /**
     * Updates the "Showing X of Y dealers" counter.
     *
     * @param {number} visibleCount
     */
    updateVisibleCount(visibleCount) {
      if (this.totalDealerCount > 0) {
        const countTemplate = this.totalDealerCount === 1 ? this.t.showingCountOne : this.t.showingCountOther;
        this.resultsText.textContent = countTemplate.replace("[current]", visibleCount).replace("[total]", this.totalDealerCount);
        this.resultsCount.removeAttribute("hidden");
      } else {
        this.resultsCount.setAttribute("hidden", "");
      }
    }

    /** Clears the search, resets filters to "both", and reloads all dealers. */
    resetFilters() {
      this.currentFilter = "both";
      this.searchCoordinates = null;
      this.selectedCountryCode = null;
      this.searchInput.value = "";

      for (const btn of this.querySelectorAll(`.${this.css.filterBtn}`)) {
        const isActive = btn.dataset.filter === "both";
        btn.classList.toggle(this.css.active, isActive);
        btn.setAttribute("aria-pressed", String(isActive));
      }

      if (this.map) {
        this.map.setView([this.config.defaultLat, this.config.defaultLng], this.config.defaultZoom);
      }
      this.loadDealers();
    }

    /** Toggles map visibility on mobile and syncs card list accordingly. */
    toggleMap() {
      this.mapWrapper.classList.toggle(this.css.visible);
      const isVisible = this.mapWrapper.classList.contains(this.css.visible);

      this.mapToggleText.textContent = isVisible ? this.t.hideMap : this.t.viewMap;

      if (isVisible && this.map) {
        this.map.invalidateSize();

        if (this.pendingLayoutCallback) {
          this.pendingLayoutCallback();
          this.pendingLayoutCallback = null;
        } else if (this.markers.length > 0) {
          this.fitMapToMarkers();
        }
      } else if (!isVisible) {
        this.syncCardsToViewport();
      }
    }

    /** Binds all event listeners and stores references for cleanup. */
    bindEventListeners() {
      this.boundOnSearchClick = () => this.onSearchClick();
      this.boundOnResetClick = () => this.resetFilters();
      this.boundOnSearchInput = (e) => this.onSearchInput(e);
      this.boundOnSearchKeydown = (e) => this.onSearchKeydown(e);
      this.boundOnFilterClick = (e) => this.onFilterClick(e);
      this.boundOnDocumentClick = (e) => this.onDocumentClick(e);
      this.boundOnMapToggle = () => this.toggleMap();
      this.boundOnDealerCardClick = (e) => this.onDealerCardAction(e);
      this.boundOnDealerCardKeydown = (e) => this.onDealerCardAction(e);
      this.boundOnSuggestionClick = (e) => this.onSuggestionClick(e);
      this.boundOnSuggestionHover = (e) => this.onSuggestionHover(e);

      this.searchBtn.addEventListener("click", this.boundOnSearchClick);
      this.resetBtn.addEventListener("click", this.boundOnResetClick);
      this.searchInput.addEventListener("input", this.boundOnSearchInput);
      this.searchInput.addEventListener("keydown", this.boundOnSearchKeydown);
      this.filterContainer.addEventListener("click", this.boundOnFilterClick);
      this.mapToggle.addEventListener("click", this.boundOnMapToggle);
      this.dealersList.addEventListener("click", this.boundOnDealerCardClick);
      this.dealersList.addEventListener("keydown", this.boundOnDealerCardKeydown);
      this.suggestionsContainer.addEventListener("click", this.boundOnSuggestionClick);
      this.suggestionsContainer.addEventListener("mouseover", this.boundOnSuggestionHover);
      document.addEventListener("click", this.boundOnDocumentClick);
    }

    /** Removes all DOM event listeners. */
    unbindEventListeners() {
      this.searchBtn.removeEventListener("click", this.boundOnSearchClick);
      this.resetBtn.removeEventListener("click", this.boundOnResetClick);
      this.searchInput.removeEventListener("input", this.boundOnSearchInput);
      this.searchInput.removeEventListener("keydown", this.boundOnSearchKeydown);
      this.filterContainer.removeEventListener("click", this.boundOnFilterClick);
      this.mapToggle.removeEventListener("click", this.boundOnMapToggle);
      this.dealersList.removeEventListener("click", this.boundOnDealerCardClick);
      this.dealersList.removeEventListener("keydown", this.boundOnDealerCardKeydown);
      this.suggestionsContainer.removeEventListener("click", this.boundOnSuggestionClick);
      this.suggestionsContainer.removeEventListener("mouseover", this.boundOnSuggestionHover);
      document.removeEventListener("click", this.boundOnDocumentClick);
    }

    /**
     * Delegated click/keydown handler for dealer cards. Opens the modal
     * for the card's dealer on click, Enter, or Space.
     *
     * @param {Event} e
     */
    onDealerCardAction(e) {
      const card = e.target.closest(`.${this.css.card}`);
      if (!card) return;
      if (e.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
      if (e.type === "keydown") e.preventDefault();
      const index = parseInt(card.dataset.dealerIndex, 10);
      if (this.dealersData[index]) {
        this.showDealerModal(this.dealersData[index], card);
      }
    }

    /**
     * Delegated click handler for suggestion items.
     *
     * @param {Event} e
     */
    onSuggestionClick(e) {
      const item = e.target.closest(`.${this.css.suggestionItem}`);
      if (!item) return;
      const index = parseInt(item.dataset.index, 10);
      if (this.suggestionResults[index]) {
        this.selectSuggestion(this.suggestionResults[index]);
      }
    }

    /**
     * Delegated hover handler for suggestion items — updates highlight.
     *
     * @param {Event} e
     */
    onSuggestionHover(e) {
      const item = e.target.closest(`.${this.css.suggestionItem}`);
      if (!item) return;
      const index = parseInt(item.dataset.index, 10);
      if (index !== this.selectedSuggestionIndex) {
        this.selectedSuggestionIndex = index;
        this.updateSuggestionHighlight();
      }
    }

    /** Validates the search input and triggers a location search. */
    onSearchClick() {
      const query = this.searchInput.value.trim();
      if (query) {
        this.clearInputError();
        this.searchLocation(query);
      } else {
        this.showInputError();
      }
    }

    /** Applies the error shake animation and marks the input as invalid. */
    showInputError() {
      // Remove + reflow + re-add to retrigger animation on repeated submits
      this.searchInput.classList.remove(this.css.inputError);
      void this.searchInput.offsetWidth;
      this.searchInput.classList.add(this.css.inputError);
      this.searchInput.setAttribute("aria-invalid", "true");
      this.searchInput.focus();
    }

    /** Removes the error state from the search input. */
    clearInputError() {
      this.searchInput.classList.remove(this.css.inputError);
      this.searchInput.removeAttribute("aria-invalid");
    }

    /**
     * Input handler — clears errors and fetches autocomplete suggestions.
     *
     * @param {Event} e
     */
    onSearchInput(e) {
      this.clearInputError();
      this.fetchSuggestions(e.target.value);
    }

    /**
     * Keyboard handler for the search input — manages arrow navigation,
     * Enter to select/search, and Escape to dismiss suggestions.
     *
     * @param {KeyboardEvent} e
     */
    onSearchKeydown(e) {
      const suggestionsVisible = !this.suggestionsContainer.hasAttribute("hidden");

      if (suggestionsVisible) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          this.navigateSuggestions("down");
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          this.navigateSuggestions("up");
        } else if (e.key === "Enter") {
          e.preventDefault();
          if (this.selectedSuggestionIndex >= 0 && this.suggestionResults[this.selectedSuggestionIndex]) {
            this.selectSuggestion(this.suggestionResults[this.selectedSuggestionIndex]);
          } else {
            this.hideSuggestions();
            this.onSearchClick();
          }
        } else if (e.key === "Escape") {
          this.hideSuggestions();
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        this.onSearchClick();
      }
    }

    /**
     * Delegated click handler for filter buttons — updates active state,
     * aria-pressed, and triggers a new search.
     *
     * @param {Event} e
     */
    onFilterClick(e) {
      const btn = e.target.closest("[data-filter]");
      if (!btn) return;

      for (const filterBtn of this.querySelectorAll(`.${this.css.filterBtn}`)) {
        filterBtn.classList.remove(this.css.active);
        filterBtn.setAttribute("aria-pressed", "false");
      }
      btn.classList.add(this.css.active);
      btn.setAttribute("aria-pressed", "true");
      this.currentFilter = btn.dataset.filter;
      this.performSearch();
    }

    /**
     * Closes the suggestions dropdown when clicking outside the search group.
     *
     * @param {Event} e
     */
    onDocumentClick(e) {
      if (!e.target.closest(`.${this.css.searchGroup}`)) {
        this.hideSuggestions();
      }
    }
  });
}
